import zoomSdk from "@zoom/appssdk";
import { 
  AIMS_STATUS, 
  ROLES, 
  OUTLOOK_WARNINGS, 
  SUMMARIZE_STATES,
  TRANSCRIPTION_STATES,
  RTMS_REQUESTER_DEFAULT_NAME, 
  AIMS_AVAILABILITY_MESSAGE_TYPES,
  AIMS_NOT_AVAILABLE_MESSAGE,
  AUTO_START_MODES,
  AVAILABILITY_TIMEOUT_ERROR,
  SESSION_STORAGE_KEYS,
} from "../../../constants";
import LogManager from "../../utils/LogManager";
import { fetchAimsAvailabilityApi } from "../../utils/AimsAvailabilityUtil";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../../utils/RtmsSessionStatusUtils";

/**
 * Process AIMS availability response and return state updates.
 * 
 * This function analyzes the AIMS availability API response to determine:
 * - User role and alias information
 * - Whether the meeting has Outlook calendar warnings
 * - Whether Germany attendees are present (affects consent requirements)
 * - The overall AIMS availability status for the meeting
 * - Auto-start mode configuration for transcription
 * 
 * The returned object contains all state values that should be applied
 * to the component state after processing.
 * 
 * @param {object} aimsAvailability - Parsed JSON object from AIMS availability API
 * @param {string} aimsAvailability.result - AIMS availability result status
 * @param {string} aimsAvailability.message - Additional message (e.g., WORK_COUNCIL, PERFORMANCE_REVIEW)
 * @param {string} aimsAvailability.warningFromOutlook - Outlook calendar warning type if any
 * @param {string} aimsAvailability.role - User's role in the meeting (ORGANIZER, ALTERNATIVE_HOST, ATTENDEE)
 * @param {string} aimsAvailability.alias - User's Amazon alias
 * @param {boolean} aimsAvailability.hasGermanyAttendee - Whether meeting has Germany-based attendees
 * @param {string} aimsAvailability.autoStartMode - Auto-start mode (SINGLE, MULTIPLE)
 * @returns {object} State updates to apply:
 *   - role: User's role
 *   - alias: User's alias
 *   - hasOutlookWarning: Whether Outlook warning exists
 *   - hasGermanyAttendee: Whether Germany attendees present
 *   - aimsStatus: AIMS availability status
 *   - aimsNotAvailableMessage: Message when AIMS not available
 *   - autoStartMode: Auto-start configuration
 */
export const handleAimsAvailabilityResult = (aimsAvailability) => {
  const result = {
    role: null,
    alias: null,
    hasOutlookWarning: false,
    hasGermanyAttendee: false,
    aimsStatus: AIMS_STATUS.AVAILABLE,
    aimsNotAvailableMessage: null,
    autoStartMode: null,
  };

  if (!aimsAvailability) {
    LogManager.warn("The aimsAvailability value is null, show exception view.");
    result.aimsStatus = AIMS_STATUS.EXCEPTION;
    return result;
  }

  const {
    result: AimsAvailableResult,
    message: AimsAvailableMessage,
    warningFromOutlook,
    role,
    alias,
    hasGermanyAttendee,
    autoStartMode,
  } = aimsAvailability;

  // Set user role information
  result.role = role;
  result.alias = alias;
  result.hasOutlookWarning = Object.values(OUTLOOK_WARNINGS).includes(warningFromOutlook);
  result.hasGermanyAttendee = hasGermanyAttendee || false;
  result.autoStartMode = autoStartMode;
  LogManager.info(`User alias: ${alias}, role: ${role}`);

  if (AimsAvailableResult === AIMS_STATUS.TRANSCRIPT_AVAILABLE) {
    LogManager.info("Transcript is available for this meeting.");
    result.aimsStatus = AIMS_STATUS.TRANSCRIPT_AVAILABLE;
    LogManager.info(`Auto start mode: ${autoStartMode}`);
  } else if (AimsAvailableResult === AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE) {
    LogManager.info("Transcript is not available for this meeting.");
    result.aimsStatus = AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE;
  } else if (AimsAvailableResult === AIMS_STATUS.AVAILABLE) {
    if (result.hasOutlookWarning) {
      LogManager.info("This meeting has MultiMeetingFoundFromOutlook/MeetingNotFoundFromOutlook warning.");
      result.aimsStatus = AIMS_STATUS.SUMMARY_TO_ORGANIZER;
    } else {
      LogManager.info("This is low risk meeting.");
      result.aimsStatus = AIMS_STATUS.AVAILABLE;
    }
  } else if (AimsAvailableResult === AIMS_STATUS.NOT_AVAILABLE) {
    LogManager.info("This is AimsNotAvailable meeting.");
    result.aimsStatus = AIMS_STATUS.NOT_AVAILABLE;
    if (AimsAvailableMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.WORK_COUNCIL) {
      result.aimsNotAvailableMessage = AIMS_NOT_AVAILABLE_MESSAGE.REGIONS;
    } else if (AimsAvailableMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.PERFORMANCE_REVIEW) {
      result.aimsNotAvailableMessage = AIMS_NOT_AVAILABLE_MESSAGE.PERFORMANCE;
    }
  } else {
    LogManager.warn("Unknown AimsAvailableResult value:", AimsAvailableResult);
    result.aimsStatus = AIMS_STATUS.EXCEPTION;
  }

  return result;
};

/**
 * Initialize Zoom SDK and fetch meeting/user context.
 * @param {object} callbacks - Object containing state setter functions
 * @returns {Promise<object>} Initialization result with context data
 */
export const initializeZoomSdk = async (callbacks) => {
  const {
    setRunningContext,
    setLastKnownContext,
    setMeetingUUID,
    setMeetingId,
    setRole,
    setAlias,
    setAimsStatus,
    setAimsNotAvailableMessage,
    setHasGermanyAttendee,
    setAutoStartMode,
    setAppContext,
    setSummarizeState,
    setTranscriptionState,
    setIsRtmsStartedByOther,
    setRtmsRequesterName,
    setRtmsStreamId,
    setIsAutostarting,
    setIsLoading,
    rtmsStartTimestampRef,
  } = callbacks;

  if (window.location.href === "http://localhost:3000/") {
    console.log("[Localhost] Skipping zoomSdk.config() in localhost");
    return { isLocalhost: true };
  }

  // Clear summaries cache on app initialization (refresh)
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA);
  LogManager.info("Cleared summaries cache on app initialization");

  try {
    const configResponse = await zoomSdk.config({
      popoutSize: { width: 362, height: 781 },
      capabilities: [
        "getAppContext",
        "getMeetingContext",
        "getRunningContext",
        "getUserContext",
        "openUrl",
        "getMeetingUUID",
        "authorize",
        "onAuthorized",
        "sendMessageToChat",
        "onRTMSStatusChange",
        "getRTMSStatus",
      ],
    });
    LogManager.info("Zoom SDK Configured Successfully:", configResponse);

    const runningContext = await zoomSdk.getRunningContext();
    LogManager.info("The runningContext is:", runningContext);

    console.log("Document Visibility State:", document.visibilityState);
    setRunningContext(runningContext.context);
    setLastKnownContext(runningContext.context);

    if (runningContext.context === "inMainClient") {
      LogManager.info("The app is running in main client now, show MainClientHome view.");
      return { runningContext: "inMainClient" };
    }

    // The runningContext is inMeeting
    const meetingUUID = (await zoomSdk.getMeetingUUID()).meetingUUID;
    console.log("The meetingUUID is:" + meetingUUID);
    setMeetingUUID(meetingUUID);

    const userContext = await zoomSdk.getUserContext();
    LogManager.info("The userContext is:", userContext);

    let aimsAvailability = null;

    // Call aimsAvailability api first to get the alias
    if (["host", "coHost"].includes(userContext.role)) {
      LogManager.info("The app is running inMeeting and user is host or coHost.");

      // Only host and coHost can call this api
      const meetingContext = await zoomSdk.getMeetingContext();
      LogManager.info("The meetingContext is: ", meetingContext);
      setMeetingId(meetingContext.meetingID);

      aimsAvailability = await fetchAimsAvailabilityApi(
        meetingUUID,
        meetingContext.meetingID,
        meetingContext.meetingTopic,
        userContext.screenName
      );
      
      const availabilityResult = handleAimsAvailabilityResult(aimsAvailability);
      setRole(availabilityResult.role);
      setAlias(availabilityResult.alias);
      setHasGermanyAttendee(availabilityResult.hasGermanyAttendee);
      setAimsStatus(availabilityResult.aimsStatus);
      setAutoStartMode(availabilityResult.autoStartMode);
      if (availabilityResult.aimsNotAvailableMessage) {
        setAimsNotAvailableMessage(availabilityResult.aimsNotAvailableMessage);
      }
      
      // Get app context for secure backend validation (only needed for organizers/alternative hosts)
      try {
        const appContextResponse = await zoomSdk.getAppContext();
        LogManager.metric("ZoomSDK_GetAppContext_Success", 1);
        LogManager.info("app context response is: " + JSON.stringify(appContextResponse));

        if (!appContextResponse || !appContextResponse.context) {
          LogManager.error("getAppContext() returned invalid response - cannot proceed with RTMS operations");
          setAimsStatus(AIMS_STATUS.EXCEPTION);
        } else {
          setAppContext(appContextResponse.context);
        }
      } catch (err) {
        LogManager.error("Failed to get app context from Zoom SDK:", err);
        LogManager.metric("ZoomSDK_GetAppContext_Failure", 1);
        setAimsStatus(AIMS_STATUS.EXCEPTION);
      }
      
      LogManager.info("Finish the initialization for host or coHost.");

    } else {
      LogManager.info("The app is running inMeeting and user is attendee.");
      setRole(ROLES.ATTENDEE);
    }

    // Check RTMS session status for all user types
    const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
    LogManager.info("RTMS status is", sessionStatus);
    
    // Process session status information for all user types
    if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
      setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
      setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
      rtmsStartTimestampRef.current = sessionStatus.rtmsStartTime;

      if (sessionStatus.rtmsStreamId) {
        setRtmsStreamId(sessionStatus.rtmsStreamId);
        LogManager.info("Saved rtmsStreamId on initialization:", sessionStatus.rtmsStreamId);
      }

      // Determine who started the session
      if (userContext.role === ROLES.ATTENDEE) {
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
      } else if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE
                 && aimsAvailability.autoStartMode === AUTO_START_MODES.MULTIPLE) {
        setIsRtmsStartedByOther(false);
      } else if (!sessionStatus.rtmsRequesterAlias) {
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
      } else if (!aimsAvailability?.alias) {
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
      } else if (sessionStatus.rtmsRequesterAlias !== aimsAvailability.alias) {
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
      } else {
        setIsRtmsStartedByOther(false);
      }
    } else {
      LogManager.info("No RTMS session found");
      setSummarizeState(SUMMARIZE_STATES.DEFAULT);
      setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
      
      // Single-stream auto-start
      if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE &&
          aimsAvailability.enableAutoStart &&
          aimsAvailability.autoStartMode === AUTO_START_MODES.SINGLE) {
        LogManager.info("Autostart conditions met: transcript available, no existing session, enableAutoStart is true and autoStartMode is single");
        setIsAutostarting(true);
      }
    }
      
    // Per-host transcription auto-start
    if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE &&
        aimsAvailability.enableAutoStart &&
        aimsAvailability.autoStartMode === AUTO_START_MODES.MULTIPLE) {
      LogManager.info("Per-host transcription autostart: transcript available, enableAutoStart is true and autoStartMode is multiple");
      setIsAutostarting(true);
    }

    return { success: true, meetingUUID, userContext, aimsAvailability };
  } catch (error) {
    LogManager.error("Error fetching context or call AimsAvailability/RTMS Status API:", error);
    setAimsStatus(error.message === AVAILABILITY_TIMEOUT_ERROR ? AIMS_STATUS.TIMEOUT : AIMS_STATUS.EXCEPTION);
    return { error };
  } finally {
    setIsLoading(false);
  }
};
